import { ChevronDown, KeyRound, LoaderCircle, X } from "lucide-react";
import type { CSSProperties } from "react";
import { useSarf39Workspace } from "./useSarf39Workspace";
import { numberFormat, plannedQuantityForDay, scheduledQuantity, type TreeNode } from "./sarf39-model";
export type { Sarf39Snapshot } from "./sarf39-model";
import "./sarf39.css";

function TreeRows({ nodes, calculationQuantity, depth = 0 }: { nodes: TreeNode[]; calculationQuantity: number; depth?: number }) {
  return (
    <>
      {nodes.map((node) => {
        const plannedConsumption = node.cumulativePerUnit * calculationQuantity;
        return (
          <div className="sarf39-tree-node" key={node.path} style={{ "--depth": depth } as CSSProperties}>
            <div className="sarf39-tree-line">
              <div>
                <strong>{node.code}</strong>
                <span>{node.name || "Ad tanımsız"}</span>
              </div>
              <div className="sarf39-tree-amount">
                <b>{numberFormat.format(node.cumulativePerUnit)}</b>
                <span>1 koli için</span>
              </div>
              <div className="sarf39-tree-plan">
                <b>{numberFormat.format(plannedConsumption)} {node.unit}</b>
                <span>{numberFormat.format(node.cumulativePerUnit)} × {numberFormat.format(calculationQuantity)}</span>
              </div>
            </div>
            {node.children.length ? <TreeRows nodes={node.children} calculationQuantity={calculationQuantity} depth={depth + 1} /> : null}
          </div>
        );
      })}
    </>
  );
}

export function Sarf39Page() {
  const { snapshot, error, pinRequired, pin, setPin, pinError, isSubmittingPin, search, setSearch, setCalculationQuantity, selectedDayKey, selectionPanelOpen, setSelectionPanelOpen, submitPin, selected, availableDays, selectedDay, filteredProducts, tree, quantity, selectProduct, selectDay } = useSarf39Workspace();

  if (pinRequired) {
    return (
      <main className="sarf39-access-page">
        <section className="sarf39-access-panel" aria-labelledby="sarf39-access-title">
          <div className="sarf39-access-icon" aria-hidden="true"><KeyRound size={28} /></div>
          <h1 id="sarf39-access-title">39. Hafta Sarf Planı</h1>
          <p>E-posta ile giriş gerekmiyor. Bu PIN yalnız bu sarf planını ve bağlı verisini açar.</p>
          <form onSubmit={submitPin} noValidate>
            <label htmlFor="sarf39-access-pin">Sayfa PIN’i</label>
            <input id="sarf39-access-pin" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 12))} type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="off" autoFocus aria-describedby={pinError ? "sarf39-access-pin-error" : undefined} aria-invalid={Boolean(pinError)} />
            {pinError ? <p id="sarf39-access-pin-error" className="sarf39-access-error" role="alert">{pinError}</p> : null}
            <button type="submit" disabled={isSubmittingPin || pin.length < 4}>
              {isSubmittingPin ? <LoaderCircle size={18} className="sarf39-spinner" aria-hidden="true" /> : <KeyRound size={18} aria-hidden="true" />}
              {isSubmittingPin ? "PIN doğrulanıyor" : "Planı aç"}
            </button>
          </form>
          <small>Bu cihazda PIN erişimi 7 gün sürer. Ortak cihazlarda kullanmayın; tarayıcıyı kapatmak erişimi sonlandırmaz.</small>
        </section>
      </main>
    );
  }

  if (error) {
    return <main className="sarf39-status"><h1>Sarf39 açılamadı</h1><p>{error}</p></main>;
  }

  if (!snapshot || !selected) {
    return <main className="sarf39-status" aria-busy="true"><h1>39. hafta sarf verisi hazırlanıyor</h1><p>Yetkiniz ve yayımlanmış veri anlık görüntüsü doğrulanıyor.</p></main>;
  }

  return (
    <main className="sarf39-page">
      <header className="sarf39-header">
        <h1>39. Hafta Sarf</h1>
      </header>

      <div className="sarf39-layout sarf39-selection-layout">
        <aside className="sarf39-products" aria-label="19'lu mamul seçimi">
          <label htmlFor="sarf39-search">19’lu mamul</label>
          <input id="sarf39-search" type="search" placeholder="Kod veya ürün ara" value={search} onChange={(event) => setSearch(event.target.value)} />
          <div className="sarf39-day-filter" aria-label="Plan günü">
            <span>Plan günü</span>
            <div role="group" aria-label="Plan gününü filtrele">
              {availableDays.map((day) => <button key={day.key} type="button" aria-pressed={selectedDayKey === day.key} onClick={() => selectDay(day.key)}>{day.label}</button>)}
              <button type="button" aria-pressed={!selectedDayKey} onClick={() => selectDay(null)}>Tüm hafta</button>
            </div>
          </div>
          <div className="sarf39-product-list">
            {filteredProducts.map((product) => (
              <button key={product.code} type="button" className={product.code === selected.code ? "is-selected" : ""} aria-pressed={product.code === selected.code} onClick={() => selectProduct(product)}>
                <strong>{product.code} - {product.lineNames.join(" / ") || "Hat tanımsız"}</strong>
                <span>{product.name}</span>
                <b>{numberFormat.format(selectedDayKey ? plannedQuantityForDay(product, selectedDayKey) : scheduledQuantity(product))} {product.unit} {selectedDay ? selectedDay.label : "bu hafta"}</b>
              </button>
            ))}
            {!filteredProducts.length ? <p className="sarf39-empty">{selectedDay ? `${selectedDay.label} için planlanan mamul yok.` : "Eşleşen mamul yok."}</p> : null}
          </div>
        </aside>

      </div>

      {selectionPanelOpen ? <div className="sarf39-selection-sheet" role="dialog" aria-modal="true" aria-labelledby="sarf39-selection-title">
        <button className="sarf39-selection-backdrop" type="button" aria-label="Seçim ayrıntısını kapat" onClick={() => setSelectionPanelOpen(false)} />
        <section className="sarf39-selection-panel">
          <header>
            <div>
              <span>{selectedDay ? `${selectedDay.label} planı` : "Tüm hafta planı"}</span>
              <h2 id="sarf39-selection-title">Sarf ihtiyacı</h2>
              <p>{selected.code} · {selected.name} · İş emri: {selected.workOrders.join(", ") || "tanımsız"}</p>
            </div>
            <button className="sarf39-selection-close" type="button" aria-label="Kapat" onClick={() => setSelectionPanelOpen(false)}><X size={20} aria-hidden="true" /></button>
          </header>
          <label className="sarf39-plan-value" htmlFor="sarf39-calculation-quantity"><span>Hesaplanacak koli</span><input id="sarf39-calculation-quantity" type="number" min="0" step="1" value={quantity} onChange={(event) => setCalculationQuantity(Number(event.target.value) || 0)} /><small>{selectedDay ? `${selectedDay.label} planı: ${numberFormat.format(plannedQuantityForDay(selected, selectedDay.key))} ${selected.unit}` : `${numberFormat.format(scheduledQuantity(selected))} ${selected.unit} günlere dağıtılan plan`}</small></label>
          <section className="sarf39-section">
            <h3>Alt ürün ve ambalaj ihtiyacı</h3>
            <p>21, 22 ve 40 kodlu alt kırılımlar aynı kod birden fazla yolda kullanılıyorsa bu mamul için birleştirilir.</p>
            <div className="sarf39-table-wrap"><table><thead><tr><th>Alt kırılım kodu</th><th>Adı</th><th>Birim</th><th>1 koli için</th><th>Hesaplanan ihtiyaç</th></tr></thead><tbody>{selected.components.map((item) => <tr key={`${item.code}-${item.unit}`}><td data-label="Alt kırılım kodu">{item.code}</td><td data-label="Adı">{item.name}</td><td data-label="Birim">{item.unit}</td><td data-label="1 koli için">{numberFormat.format(item.perUnit)}</td><td data-label="Hesaplanan ihtiyaç"><strong>{numberFormat.format(item.perUnit * quantity)} {item.unit}</strong></td></tr>)}</tbody></table>{!selected.components.length ? <p className="sarf39-empty">Bu mamul için 21, 22 veya 40 kodlu alt kırılım bulunamadı.</p> : null}</div>
          </section>
          <details className="sarf39-tree-details">
            <summary><span>Ürün ağacını incele</span><small>21, 22 ve 40 kodlu alt kırılımlar</small><ChevronDown size={18} aria-hidden="true" /></summary>
            <div className="sarf39-tree"><TreeRows nodes={tree} calculationQuantity={quantity} /></div>
          </details>
          <footer className="sarf39-source">Kaynak: {snapshot.source.plan} · {snapshot.source.note}</footer>
        </section>
      </div> : null}
    </main>
  );
}
